import React, { useCallback, useEffect, useState } from 'react';
import { useSupabaseAuth } from '../contexts/SupabaseAuthContext';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import CustomerServiceCaseCard from '../components/customerService/CustomerServiceCaseCard';
import DisputeRequestComposer from '../components/customerService/DisputeRequestComposer';
import {
  fetchCustomerServiceCases,
  fetchMyDisputeRequests,
  submitDisputeRequest
} from '../services/customerServiceService';

const { FiLoader, FiRefreshCw, FiShield } = FiIcons;

const CustomerServiceDashboardPage = () => {
  const { user, profile, loading } = useSupabaseAuth();
  const [cases, setCases] = useState([]);
  const [requests, setRequests] = useState([]);
  const [selected, setSelected] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [loaded, setLoaded] = useState(false);

  const refresh = useCallback(async () => {
    setError('');
    try {
      const [caseRows, requestRows] = await Promise.all([
        fetchCustomerServiceCases(),
        fetchMyDisputeRequests()
      ]);
      setCases(caseRows || []);
      setRequests(requestRows || []);
    } catch (requestError) {
      setError(requestError.message || 'Unable to load customer-service cases.');
    } finally {
      setLoaded(true);
    }
  }, []);

  useEffect(() => {
    if (profile?.role === 'customer_service') refresh();
  }, [profile, refresh]);

  const sendRequest = async (details) => {
    if (!selected) return;
    setBusy(true);
    setError('');
    try {
      await submitDisputeRequest({ disputeId: selected.dispute_id, ...details });
      setSelected(null);
      await refresh();
    } catch (requestError) {
      setError(requestError.message || 'Unable to send the administrator request.');
    } finally {
      setBusy(false);
    }
  };

  if (loading || !loaded && profile?.role === 'customer_service') {
    return <div className="flex min-h-[60vh] items-center justify-center"><SafeIcon icon={FiLoader} className="animate-spin text-3xl text-indigo-600" /></div>;
  }

  if (!user || profile?.role !== 'customer_service') {
    return (
      <div className="mx-auto my-16 max-w-xl rounded-3xl border border-slate-100 bg-white p-10 text-center shadow-sm">
        <SafeIcon icon={FiShield} className="mx-auto text-3xl text-rose-500" />
        <h1 className="mt-4 text-xl font-black text-slate-900">Customer service access required</h1>
        <p className="mt-2 text-sm text-slate-500">An administrator must assign the customer-service role to this account.</p>
      </div>
    );
  }

  return (
    <main className="min-h-screen bg-slate-50 px-4 py-10">
      <div className="mx-auto max-w-6xl">
        <header className="mb-8 flex flex-wrap items-center justify-between gap-4">
          <div>
            <p className="text-[10px] font-black uppercase tracking-[0.25em] text-indigo-500">LocalMarket Support</p>
            <h1 className="mt-2 text-3xl font-black tracking-tight text-slate-900">Customer Service Dashboard</h1>
            <p className="mt-2 text-sm text-slate-500">Review order issues and ask an administrator to approve a resolution.</p>
          </div>
          <button type="button" onClick={refresh} className="flex items-center gap-2 rounded-xl bg-white px-4 py-3 text-xs font-black uppercase text-indigo-600 shadow-sm">
            <SafeIcon icon={FiRefreshCw} /> Refresh cases
          </button>
        </header>

        {error && <p className="mb-5 rounded-xl bg-rose-50 p-4 text-sm font-bold text-rose-700">{error}</p>}

        <section className="mb-8 rounded-2xl border border-slate-100 bg-white p-5 shadow-sm">
          <h2 className="font-black text-slate-900">My submitted requests</h2>
          {requests.length ? (
            <ul className="mt-3 space-y-2">
              {requests.map((request) => (
                <li key={request.id} className="flex flex-wrap justify-between gap-2 rounded-xl bg-slate-50 p-3 text-xs">
                  <span className="font-bold text-slate-700">Order #{request.order_id.slice(0, 8).toUpperCase()} · {request.recommendation.replaceAll('_', ' ')}</span>
                  <span className={`font-black uppercase ${request.status === 'approved' ? 'text-emerald-600' : request.status === 'rejected' ? 'text-rose-600' : 'text-amber-600'}`}>{request.status}</span>
                </li>
              ))}
            </ul>
          ) : <p className="mt-2 text-sm text-slate-500">No requests submitted yet.</p>}
        </section>

        <div className="grid gap-6 lg:grid-cols-[1.2fr_0.8fr]">
          <section className="space-y-4">
            <h2 className="text-lg font-black text-slate-900">Order disputes ({cases.length})</h2>
            {cases.length ? cases.map((item) => (
              <CustomerServiceCaseCard
                key={item.dispute_id}
                item={item}
                selected={selected?.dispute_id === item.dispute_id}
                onSelect={() => setSelected(item)}
              />
            )) : <p className="rounded-2xl bg-white p-8 text-center text-sm text-slate-500">No disputes are available.</p>}
          </section>
          <aside>
            {selected ? (
              <DisputeRequestComposer
                key={selected.dispute_id}
                dispute={selected}
                busy={busy}
                onSubmit={sendRequest}
                onCancel={() => setSelected(null)}
              />
            ) : (
              <div className="rounded-2xl border border-dashed border-slate-300 p-8 text-center text-sm text-slate-500">
                Select a dispute to prepare a request for administrator approval.
              </div>
            )}
          </aside>
        </div>
      </div>
    </main>
  );
};

export default CustomerServiceDashboardPage;
